import { motion } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SALON_INFO, SALON_PHOTOS } from "../data/assets";

export const Hero = () => {
  const { t } = useLang();

  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen w-full overflow-hidden flex items-end"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center kenburns"
          style={{ backgroundImage: `url(${SALON_PHOTOS.hero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/55 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/70 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32 pt-32">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.18, delayChildren: 0.4 } },
          }}
          className="max-w-4xl"
        >
          {/* Eyebrow */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-10 bg-[#C5A059]" />
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">
              {t.hero.eyebrow}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={{
              hidden: { opacity: 0, y: 30 },
              show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
            }}
            className="font-display text-white leading-[0.95] tracking-tight"
            data-testid="hero-title"
          >
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] font-medium">
              {t.hero.title1}
            </span>
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-9xl italic font-light text-[#C5A059] -mt-2 md:-mt-4">
              {t.hero.title2}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
            className="mt-8 max-w-xl text-base md:text-lg text-white/75 font-light leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.tagline}
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 20 },
              show: { opacity: 1, y: 0, transition: { duration: 0.8 } },
            }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={SALON_INFO.notino}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-3 bg-[#C5A059] text-black text-xs sm:text-sm uppercase tracking-[0.25em] px-8 py-5 hover:bg-[#E8D3A6] transition-all duration-300 gold-glow"
              data-testid="hero-reserve-btn"
            >
              <span>{t.hero.cta_reserve}</span>
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
            <a
              href={`tel:${SALON_INFO.phoneIntl}`}
              className="inline-flex items-center justify-center gap-3 border border-[#C5A059]/60 text-[#C5A059] text-xs sm:text-sm uppercase tracking-[0.25em] px-8 py-5 hover:bg-[#C5A059]/10 transition-all duration-300"
              data-testid="hero-call-btn"
            >
              <Phone size={16} strokeWidth={1.5} />
              <span>{t.hero.cta_call} · {SALON_INFO.phone}</span>
            </a>
          </motion.div>
        </motion.div>

        {/* Bottom row: rating + scroll */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-20 flex items-end justify-between gap-8 flex-wrap"
        >
          <div className="flex items-center gap-6">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-[#C5A059] text-lg">★</span>
              ))}
            </div>
            <div className="border-l border-white/15 pl-6">
              <div className="font-display text-2xl text-white">{SALON_INFO.rating}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                Google · {SALON_INFO.reviews} recenzií
              </div>
            </div>
          </div>

          <button
            onClick={() => go("about")}
            className="hidden md:flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#C5A059] transition-colors"
            data-testid="hero-scroll-btn"
          >
            <span>{t.hero.scroll}</span>
            <motion.span
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            >
              <ChevronDown size={18} strokeWidth={1.5} />
            </motion.span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};
